import { DatePipe, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslocoModule } from '@jsverse/transloco';
import { FormFieldComponent, InputDirective, LabelDirective, WhButtonComponent } from '@wheere-front/shared-ui';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { interval, map, startWith } from 'rxjs';
import { BaroValue, LastBaro } from '../data/baro.model';
import { barometerSparkline } from '../util/barometer-sparkline';

dayjs.extend(relativeTime);

/** Position fixe d'une ancre barométrique (`trackers.fixed_*`). */
export interface BarometerFixedPosition {
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
}

/** Position soumise par le formulaire (latitude/longitude obligatoires). */
export interface BarometerPositionSubmit {
  latitude: number;
  longitude: number;
  altitude: number | null;
}

/**
 * Fiche « Barometer » d'un appareil `kind = BAROMETER` (ADR-020), partagée inside / integrator :
 * dernière pression / température / batterie avec leur horodatage, sparkline des pressions reçues
 * en live sur la page, et position fixe de l'ancre. Présentationnel : l'appelant fournit les
 * mesures (REST + STOMP `/baro`) et, s'il autorise l'édition, enregistre la position soumise
 * (`PUT /trackers/{id}/barometer-position`). Sans édition, la position est affichée en lecture.
 */
@Component({
  selector: 'lib-barometer-panel',
  standalone: true,
  imports: [
    DecimalPipe,
    DatePipe,
    ReactiveFormsModule,
    TranslocoModule,
    FormFieldComponent,
    InputDirective,
    LabelDirective,
    WhButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(280px,2fr)]">
      <!-- Last reading + sparkline -->
      <div class="rounded-xl border border-gray-200 bg-white p-4 md:p-5 dark:border-slate-700 dark:bg-slate-800">
        <div class="mb-1 flex items-center gap-2">
          <i class="pi pi-gauge text-teal-500"></i>
          <h2 class="m-0 text-base font-semibold text-gray-800 dark:text-slate-100">
            {{ 'barometerPanel.lastReadings' | transloco }}
          </h2>
          @if (live()) {
            <span
              class="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-teal-600">
              <span class="size-1.5 animate-pulse rounded-full bg-teal-500"></span>
              {{ 'barometerPanel.live' | transloco }}
            </span>
          }
        </div>
        @if (hasReading()) {
          <div class="mt-2 grid gap-3 sm:grid-cols-3">
            @for (q of quantities(); track q.key) {
              <div>
                <div class="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400">
                  {{ q.labelKey | transloco }}
                </div>
                @if (q.v; as v) {
                  <div
                    class="tabular-nums text-gray-900 dark:text-slate-50"
                    [class]="q.key === 'pressure' ? 'text-4xl font-bold' : 'text-2xl font-semibold'">
                    {{ v.value | number: q.format }}
                    <span class="text-base font-normal text-gray-500">{{ q.unit }}</span>
                  </div>
                  <div class="mt-0.5 text-xs text-gray-500 dark:text-slate-400" [title]="v.timestamp">
                    {{ v.timestamp | date: 'dd/MM/yyyy - HH:mm:ss' }} · {{ ago(v) }}
                  </div>
                } @else {
                  <div class="text-2xl text-gray-300 dark:text-slate-600">—</div>
                  <div class="mt-0.5 text-xs text-gray-400">{{ 'barometerPanel.neverReceived' | transloco }}</div>
                }
              </div>
            }
          </div>
        } @else {
          <p class="mt-2 text-sm text-gray-500 dark:text-slate-400">
            {{ 'barometerPanel.noReading' | transloco }} <code>tk/baro/…/to_srv</code>.
          </p>
        }

        @if (sparkline(); as sp) {
          <svg
            class="mt-4 h-24 w-full text-teal-500"
            [attr.viewBox]="'0 0 ' + sp.w + ' ' + sp.h"
            preserveAspectRatio="none">
            <path [attr.d]="sp.area" fill="currentColor" opacity="0.12" />
            <path
              [attr.d]="sp.line"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              vector-effect="non-scaling-stroke" />
          </svg>
          <div class="mt-1 flex justify-between text-[11px] tabular-nums text-gray-400">
            <span>{{ sp.min | number: '1.1-1' }} hPa</span>
            <span>{{ 'barometerPanel.readingsOnPage' | transloco: { count: sp.count } }}</span>
            <span>{{ sp.max | number: '1.1-1' }} hPa</span>
          </div>
        } @else {
          <p class="mt-4 text-xs text-gray-400">{{ 'barometerPanel.sparklineHint' | transloco }}</p>
        }
      </div>

      <!-- Fixed position of the anchor -->
      <div class="rounded-xl border border-gray-200 bg-white p-4 md:p-5 dark:border-slate-700 dark:bg-slate-800">
        <div class="mb-1 flex items-center gap-2">
          <i class="pi pi-map-marker text-primary-500"></i>
          <h2 class="m-0 text-base font-semibold text-gray-800 dark:text-slate-100">
            {{ 'barometerPanel.position' | transloco }}
          </h2>
        </div>
        <p class="mb-4 text-sm text-gray-500 dark:text-slate-400">
          {{ 'barometerPanel.positionHint' | transloco }}
        </p>
        @if (editablePosition()) {
          <form [formGroup]="form" (ngSubmit)="onSubmit()" class="grid grid-cols-2 gap-3">
            <lib-form-field>
              <label for="latitude" libLabel variant="floating">{{ 'barometerPanel.latitude' | transloco }}</label>
              <input
                libInput
                id="latitude"
                type="number"
                step="0.000001"
                min="-90"
                max="90"
                formControlName="latitude"
                class="w-full" />
            </lib-form-field>
            <lib-form-field>
              <label for="longitude" libLabel variant="floating">{{ 'barometerPanel.longitude' | transloco }}</label>
              <input
                libInput
                id="longitude"
                type="number"
                step="0.000001"
                min="-180"
                max="180"
                formControlName="longitude"
                class="w-full" />
            </lib-form-field>
            <lib-form-field class="col-span-2">
              <label for="altitude" libLabel variant="floating">{{ 'barometerPanel.altitude' | transloco }}</label>
              <input libInput id="altitude" type="number" step="0.1" formControlName="altitude" class="w-full" />
            </lib-form-field>
            <div class="col-span-2 flex justify-end">
              <wh-button size="sm" type="submit" [disabled]="form.invalid || form.pristine || saving()">
                {{ (saving() ? 'barometerPanel.saving' : 'barometerPanel.savePosition') | transloco }}
              </wh-button>
            </div>
          </form>
        } @else {
          <div class="grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-2 text-sm">
            <div class="text-gray-500 dark:text-slate-400">{{ 'barometerPanel.latitude' | transloco }}</div>
            <div class="text-right font-semibold tabular-nums dark:text-slate-100">
              {{ hasValue(fixedPosition()?.latitude) ? (fixedPosition()?.latitude | number: '1.6-6') : '—' }}
            </div>
            <div class="text-gray-500 dark:text-slate-400">{{ 'barometerPanel.longitude' | transloco }}</div>
            <div class="text-right font-semibold tabular-nums dark:text-slate-100">
              {{ hasValue(fixedPosition()?.longitude) ? (fixedPosition()?.longitude | number: '1.6-6') : '—' }}
            </div>
            <div class="text-gray-500 dark:text-slate-400">{{ 'barometerPanel.altitude' | transloco }}</div>
            <div class="text-right font-semibold tabular-nums dark:text-slate-100">
              {{ hasValue(fixedPosition()?.altitude) ? (fixedPosition()?.altitude | number: '1.0-1') + ' m' : '—' }}
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class BarometerPanelComponent {
  /** Dernière mesure par grandeur (REST `lastBaro`, fusionnée avec le live par l'appelant). */
  readonly lastBaro = input<LastBaro | null | undefined>(null);
  /** Pressions (hPa) reçues en live sur cette page, la plus récente en dernier. */
  readonly pressureHistory = input<readonly number[]>([]);
  readonly fixedPosition = input<BarometerFixedPosition | null | undefined>(null);
  /** Formulaire de position (inside) ou lecture seule (integrator). */
  readonly editablePosition = input(false);
  readonly saving = input(false);
  readonly positionSubmit = output<BarometerPositionSubmit>();

  #fb = inject(FormBuilder);

  form = this.#fb.nonNullable.group({
    latitude: [null as number | null, [Validators.required, Validators.min(-90), Validators.max(90)]],
    longitude: [null as number | null, [Validators.required, Validators.min(-180), Validators.max(180)]],
    altitude: [null as number | null],
  });

  protected readonly hasReading = computed(() => {
    const l = this.lastBaro() ?? {};
    return !!(l.pressure || l.temperature || l.battery);
  });
  protected readonly quantities = computed(() => {
    const l = this.lastBaro() ?? {};
    return [
      { key: 'pressure', labelKey: 'barometerPanel.pressure', unit: 'hPa', format: '1.1-1', v: l.pressure },
      { key: 'temperature', labelKey: 'barometerPanel.temperature', unit: '°C', format: '1.1-1', v: l.temperature },
      { key: 'battery', labelKey: 'barometerPanel.battery', unit: '%', format: '1.0-0', v: l.battery },
    ];
  });
  protected readonly live = computed(() => this.pressureHistory().length > 0);
  protected readonly sparkline = computed(() => barometerSparkline(this.pressureHistory()));

  /** Tick 30 s : « il y a 2 min » vieillit sans nouvelle trame. */
  readonly #now = toSignal(
    interval(30_000).pipe(
      startWith(0),
      map(() => Date.now())
    ),
    { initialValue: Date.now() }
  );
  /** Lit `#now` : réévalué par le template à chaque tick. */
  protected ago(v: BaroValue): string {
    this.#now();
    return dayjs(v.timestamp).fromNow();
  }

  protected hasValue(v: number | null | undefined): v is number {
    return v !== null && v !== undefined;
  }

  constructor() {
    // Le formulaire reflète la position sauvée (y compris après un save, via l'entité de l'appelant).
    effect(() => {
      const p = this.fixedPosition();
      this.form.reset(
        {
          latitude: p?.latitude ?? null,
          longitude: p?.longitude ?? null,
          altitude: p?.altitude ?? null,
        },
        { emitEvent: false }
      );
    });
  }

  onSubmit(): void {
    const v = this.form.getRawValue();
    if (this.form.invalid || v.latitude == null || v.longitude == null) return;
    this.positionSubmit.emit({
      latitude: Number(v.latitude),
      longitude: Number(v.longitude),
      altitude: v.altitude == null ? null : Number(v.altitude),
    });
  }
}
